/**
 * Sidebar/hooks/useNavAccess/index.ts
 * desc: decides which navigation items and actions the current user may see
 * note: an item's visibility is its target route's own auth check (router/utilities) plus its feature flag —
 *       the same test AuthorizedRoute runs, so a visible link can never 403 and an allowed page is never hidden;
 *       an action has no route, so it carries its own permissions
 */

import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import routes from '../../../../routes';
import { isRouteAuthorized } from '../../../../router/utilities';
import { hasAccess } from '../../../../utilities/authentication';
import { getNavItemPath } from '../../utilities';
import type { AppRoute } from '../../../../router/types';
import type { RootState } from '../../../../store';
import type { NavAction, NavItem } from '../../config';

// path → route, from the same table AppRouter renders
const routesByPath = new Map<string, AppRoute>();
routes.appRoutes.forEach((route) => {
    if (route.path) routesByPath.set(route.path, route);
});

/**
 * getNavItemRoute
 * desc: the route configuration an item points at, or undefined when no route serves its app-urls key
 */
export function getNavItemRoute(item: NavItem): AppRoute | undefined {
    return routesByPath.get(getNavItemPath(item));
}

const useNavAccess = () => {
    const permissions = useSelector((state: RootState) => state.users.me?.permissions);
    const flags = useFlags();

    const canAccess = useCallback((item: NavItem): boolean => {
        if (item.flag && !flags[item.flag]) return false;

        const route = getNavItemRoute(item);
        if (!route) {
            console.error(`Sidebar: no route is registered for "${item.appUrlKey}"`);
            return false;
        }

        return isRouteAuthorized(route, permissions || []);
    }, [ flags, permissions ]);

    const canAccessAction = useCallback((action: NavAction): boolean => {
        if (action.flag && !flags[action.flag]) return false;

        return hasAccess(action.permissions, permissions || []);
    }, [ flags, permissions ]);

    return { canAccess, canAccessAction };
};

export default useNavAccess;
